import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Users,
  BarChart3,
  Code,
  Bug,
  Target,
  Briefcase,
  Network,
  GraduationCap,
  Brain,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { services } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const iconMap = { Shield, Users, BarChart3, Code, Bug, Target, Briefcase, Network, GraduationCap, Brain };

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function ServicesPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            Practice Areas
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Our Practice Areas
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            Comprehensive cybersecurity advisory practice areas designed to address the full spectrum of
            enterprise security challenges with strategic precision.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, idx) => {
              const Icon = iconMap[service.icon];
              return (
                <Reveal key={service.id} delay={idx * 60}>
                  <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden hover:shadow-md transition-shadow duration-300">
                    {/* Service Image */}
                    {service.image && (
                      <div className="relative h-48 lg:h-52 overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D33]/60 via-[#0B1D33]/20 to-transparent" />
                        <div className="absolute bottom-4 left-6 flex items-center gap-3">
                          {Icon && (
                            <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center">
                              <Icon className="h-4.5 w-4.5 text-white" strokeWidth={1.5} />
                            </div>
                          )}
                          <h2 className="text-lg font-semibold text-white drop-shadow-sm">
                            {service.title}
                          </h2>
                        </div>
                      </div>
                    )}

                    <div className="p-8 lg:p-10">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Left Column */}
                        <div className="lg:col-span-7">
                          {service.frameworks && service.frameworks.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-5">
                              {service.frameworks.map((fw) => (
                                <Badge
                                  key={fw}
                                  variant="secondary"
                                  className="text-[10px] font-medium uppercase tracking-wider bg-slate-100 text-slate-500 hover:bg-slate-100"
                                >
                                  {fw}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <p className="text-slate-500 leading-relaxed text-[15px]">
                            {service.description}
                          </p>
                        </div>

                        {/* Right Column - Deliverables */}
                        <div className="lg:col-span-5">
                        <div className="lg:pl-8 lg:border-l border-slate-100">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-4">
                            Key Deliverables
                          </h4>
                          <ul className="space-y-2.5">
                            {service.deliverables.map((item, i) => (
                              <li key={i} className="flex items-start gap-2.5">
                                <Check className="h-4 w-4 text-[#3B82A0] mt-0.5 flex-shrink-0" strokeWidth={2} />
                                <span className="text-sm text-slate-600">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="bg-[#0B1D33] rounded-xl p-10 lg:p-16 text-center relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern" />
              <div className="relative">
                <h2 className="text-2xl md:text-3xl font-light text-white mb-5 tracking-tight">
                  Let's Discuss Your Needs
                </h2>
                <p className="text-slate-300/80 max-w-xl mx-auto mb-8 leading-relaxed">
                  Every engagement begins with understanding your unique challenges. Reach out to
                  explore how our practice areas can support your objectives.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#3B82A0] hover:bg-[#2D6A85] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200">
                    Request a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
